import { DiwaAPI } from './client'

/**
 * Mirrors GET /credits in products/diwa (billing/credits_router.py) — the ONE suite
 * credit. There is a single suite-wide currency (distinct from the per-plan enrichment
 * / draft meters); this is the economy earned from milestones. EARN is milestone-driven
 * and idempotent (a sigil credits once, keyed by `reason`); SPEND is deferred. The
 * balance is the running total and the ledger is append-only, newest first, owner-scoped.
 */
export interface CreditLedgerRow {
  /** Signed change this row applied (+100 for a grant; negative once spend ships). */
  delta: number
  /** The idempotency key / cause — e.g. "sigil:streak-7". Prettify via creditReasonLabel. */
  reason: string
  /** Running balance after this row was written. */
  balance_after: number
  /** ISO timestamp the row was written, or null. */
  created_at: string | null
}

export interface Credits {
  /** Current suite-credit balance (sum of deltas). */
  balance: number
  /** Recent rows, newest first (owner-scoped). */
  ledger: CreditLedgerRow[]
}

// The known milestone reasons map to a plain, keeper-facing phrase. Reasons are
// "sigil:<key>"; anything unmapped falls back to a de-kebabbed key so a new milestone
// still reads sensibly without a frontend change.
const REASON_LABELS: Record<string, string> = {
  'sigil:first-world': 'first world',
  'sigil:first-spring': 'first spring',
  'sigil:first-signal': 'first signal',
  'sigil:first-mission': 'first mission',
  'sigil:streak-7': '7-day streak',
  'sigil:curator': 'curator sigil',
  'sigil:qa': 'keeper of the record',
  'sigil:founding-keeper': 'founding keeper',
}

/** A ledger row's `reason` -> the short human phrase shown next to its delta. */
export function creditReasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/^sigil:/, '').replace(/-/g, ' ')
}

/** A signed delta -> "+100" / "-50" for the ledger line. */
export function formatDelta(delta: number): string {
  return delta >= 0 ? `+${delta}` : `${delta}`
}

export class CreditsAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  /** GET /credits — your suite-credit balance and recent ledger, newest first. */
  get(): Promise<Credits> {
    return this.api.get<Credits>('/credits')
  }
}
